import convertSubstrateSuggestionsResponseToSuggestionSet from './convertSubstrateSuggestionsResponseToSuggestionSet';
import { getStore as getMailSearchStore } from '../../store/store';
import { PerformanceDatapoint } from 'owa-analytics';
import { GenericKeys } from 'owa-analytics/lib/types/DatapointEnums';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { SearchScenarioId } from 'owa-search-next';
import {
    lazyGetSearchStore,
    lazyIsSearchBoxEmpty,
    onReceivedSuggestionsToRender,
    onServiceResponse,
} from 'owa-search-next-lazy';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import {
    getLocalTime,
    substrateSearchSuggestionsService,
    lazyLogResponseReceived,
    SubstrateSearchSuggestionsResponse,
    lazyLogResponseNeverRendered,
    SearchScope,
    SearchScopeKind,
    SuggestionSet,
    SubstrateSearchScenario,
} from 'owa-search-service';
import isInLazySearchExperiment from '../../utils/isInLazySearchExperiment';

/**
 * Gets suggestions from 3S.
 */
export default async function getSubstrateSuggestions(
    scenarioId: SearchScenarioId,
    queryStringForSuggestions: string
): Promise<SuggestionSet> {
    const getSearchStore = await lazyGetSearchStore.import();
    const searchStore = getSearchStore(scenarioId);

    const suggestionsRequestTime = new Date();

    const customHeaders = {
        'client-session-id': searchStore.searchSessionGuid,
        'x-client-localtime': getLocalTime(),
        'client-request-id': searchStore.latestQFRequestId,
    };

    // Add "x-user-context" header if we can create the user context.
    const userContext = getUserScopeContext(getMailSearchStore().staticSearchScope);
    if (userContext) {
        customHeaders['x-user-context'] = userContext;
    }

    // Start datapoint to track suggestions request latency.
    const networkPerformanceDatapoint = new PerformanceDatapoint('TnS_Autosuggestion3S');

    // Get 3S scenario ID based on search box scenario ID.
    const substrateSearchScenario =
        scenarioId === SearchScenarioId.Mail
            ? SubstrateSearchScenario.Mail
            : SubstrateSearchScenario.FilesHub;

    const supportedSegmentTypes = getSupportedSegmentTypes();

    const substrateSuggestionsResponse = await substrateSearchSuggestionsService(
        queryStringForSuggestions,
        searchStore.searchSessionGuid,
        searchStore.nextSearchQueryId,
        getSupportedEntityTypes(),
        substrateSearchScenario,
        supportedSegmentTypes,
        customHeaders
    );

    // End datapoint to track suggestions request latency.
    networkPerformanceDatapoint.end();

    const emptySuggestionSet = {
        Suggestions: [],
        TraceId: searchStore.latestQFRequestId,
        IsComplete: true,
    };

    /**
     * If we don't get a valid response, return empty suggestion set. We
     * defensively check for a falsey object, but also check if the response
     * is an array (as that's what the makeServiceRequest function returns when
     * there's a fetch error).
     */
    if (!substrateSuggestionsResponse || Array.isArray(substrateSuggestionsResponse)) {
        return emptySuggestionSet;
    }

    lazyLogResponseReceived.importAndExecute(
        substrateSuggestionsResponse.headers.get('request-id'),
        <number>networkPerformanceDatapoint.getData(GenericKeys.e2eTimeElapsed),
        substrateSuggestionsResponse.status,
        new Date()
    );

    try {
        const substrateSuggestionsResponseBody = <SubstrateSearchSuggestionsResponse>(
            await substrateSuggestionsResponse.json()
        );

        onServiceResponse(
            scenarioId,
            substrateSuggestionsResponseBody.Instrumentation.TraceId,
            true /* isQFTraceId */
        );

        // Parse response into SuggestionSet.
        const suggestionSet = substrateSuggestionsResponseBody.Groups
            ? convertSubstrateSuggestionsResponseToSuggestionSet(substrateSuggestionsResponseBody)
            : {
                  Suggestions: [],
                  TraceId: substrateSuggestionsResponseBody.Instrumentation.TraceId,
                  IsComplete: true,
              };

        /**
         * Check suggestionsRequestTime against displayedQfRequestTime,
         * and if newer, than return.
         *
         * Also check if the search box isn't empty. This ensures that
         * zero input suggestions are shown in the case where a user types a query
         * and then erases it.
         *
         * If not, log "responseneverrendered" event and return whatever is currently
         * in the store (since those suggestions are newer).
         */
        const isSearchBoxEmpty = await lazyIsSearchBoxEmpty.import();
        const displayedQFRequestTime = searchStore.displayedQFRequestTime;
        if (
            (!displayedQFRequestTime ||
                suggestionsRequestTime.getTime() > displayedQFRequestTime.getTime()) &&
            (!isSearchBoxEmpty(scenarioId) ||
                (is3SFeatureEnabled('sea-zinq-3s3') || isInLazySearchExperiment()) ||
                is3SFeatureEnabled('sea-zinq-3sctr3'))
        ) {
            if (!is3SFeatureEnabled('sea-zinq-3sctr3')) {
                onReceivedSuggestionsToRender(scenarioId, suggestionsRequestTime);
            }
            return suggestionSet;
        } else {
            lazyLogResponseNeverRendered.importAndExecute(
                suggestionSet.TraceId,
                substrateSearchScenario
            );

            // Return most recent, valid suggestions (if any exist).
            return searchStore.currentSuggestions || emptySuggestionSet;
        }
    } catch (e) {
        /**
         * In some cases (i.e. 401 from server) we will not have a body so return
         * empty suggestion set
         */
        return emptySuggestionSet;
    }
}

function getSupportedEntityTypes() {
    const supportedEntityTypes: string[] = ['Text', 'People'];
    if (is3SFeatureEnabled('sea-emailSuggestions')) {
        supportedEntityTypes.push('Message');
    }

    if (is3SFeatureEnabled('sea-filesSuggestions')) {
        supportedEntityTypes.push('Attachment');
    }

    if (is3SFeatureEnabled('sea-calendarSuggestions')) {
        supportedEntityTypes.push('Event');
    }

    return supportedEntityTypes.join(',');
}

function getSupportedSegmentTypes(): string {
    const supportedSegmentTypes: string[] = [];

    if (is3SFeatureEnabled('sea-gf')) {
        supportedSegmentTypes.push('text', 'person');
    }

    return supportedSegmentTypes.join(',');
}

/**
 * This function is used to get the value for the "x-user-context" header,
 * which is used by 3S to send back the appropriate KQL for people suggestions.
 */
function getUserScopeContext(staticSearchScope: SearchScope) {
    if (staticSearchScope) {
        switch (staticSearchScope.kind) {
            case SearchScopeKind.PrimaryMailbox:
            case SearchScopeKind.ArchiveMailbox:
                const folderId = staticSearchScope.folderId;
                const folderName = folderIdToName(folderId);
                return folderName !== 'none'
                    ? `DistinguishedFolderName=${folderName};FolderId=${folderId}`
                    : `FolderId=${folderId}`;
            default:
                return null;
        }
    }

    return null;
}
